import React from 'react';

import {
  Description as PDF,
  PlayCircleFilledOutlined as Video,
  InsertDriveFileRounded as Document,
  InsertDriveFileRounded as Binary,
} from '@material-ui/icons';
import { Typography, Box } from '@material-ui/core';

import { FileResponse } from './index';
import { ExpandedFileFileResponse } from './index.private.interfaces';

import { FileSelectorData } from './FileSelectorContext';

export interface FilePreviewProps {
  file: FileResponse;
  selected?: boolean;
  disableText?: boolean;
}

export interface FilePreviewExpandedProps {
  file: ExpandedFileFileResponse;
  onClose?: (setter: undefined) => void;
}

const filePreview = {
  file: {
    transition: 'transform 0.2s ease-in-out',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    overflow: 'hidden',
  },
  noFile: {
    transition: 'transform 0.2s ease-in-out',
    width: '85%',
    height: '85%',
    objectFit: 'cover',
    overflow: 'hidden',
  },
  pdfFile: {
    width: `calc(100% + ${16 + 6 + 2}px)`,
    height: `calc(100% + ${6}px)`,
    marginTop: '-6px',
    marginLeft: '-6px',
    position: 'absolute',
    left: 0,
    top: 0,
    objectFit: 'cover',
    pointerEvents: 'none',
  },
  textWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    whiteSpace: 'nowrap',
    background: '#444',
  },
  text: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#09a9ee',
    marginLeft: 1,
    marginRight: 1,
    textTransform: 'initial',
  },
} as const;

export const FilePreview = (props: FilePreviewProps) => {
  const fileSelectorData = React.useContext(FileSelectorData);

  const titledImage = (children: React.ReactChild) => {
    return (
      <React.Fragment>
        {children}
        {!props.disableText && (
          <Box sx={filePreview.textWrapper}>
            <Typography style={filePreview.text} variant="subtitle2">
              {props.file.displayName}
            </Typography>
          </Box>
        )}
      </React.Fragment>
    );
  };

  const displayedImage = (file: FileResponse) => {
    switch (file.filetype) {
      case 'image':
      case 'vectorgraphic':
        return (
          <img
            src={fileSelectorData.rootPath + (fileSelectorData.previewImage?.(file.path) || file.path)}
            style={{
              transform: `scale(${props.selected ? 0.9 : 1})`,
              ...filePreview.file,
            }}
            loading="lazy"
            draggable={false}
            alt={file.displayName}
          />
        );
      case 'document':
        return titledImage(<Document style={filePreview.noFile} />);
      case 'pdf':
        return titledImage(
          navigator.userAgent.includes('Chrome') ? (
            <Box
              sx={filePreview.file}
              style={{
                transform: `scale(${props.selected ? 0.9 : 1})`,
              }}
              draggable={false}
            >
              <iframe
                style={filePreview.pdfFile}
                src={
                  fileSelectorData.rootPath +
                  (fileSelectorData.previewImage?.(file.path) || file.path) +
                  '#scrollbar=0&toolbar=0&navpanes=0&view=FitH&zoom=FitH'
                }
                loading="lazy"
                width="100%"
                height="100%"
                title={file.displayName}
              />
            </Box>
          ) : (
            <PDF style={filePreview.noFile} />
          ),
        );
      case 'video':
        return titledImage(<Video style={filePreview.noFile} />);
      default:
        return titledImage(<Binary style={filePreview.noFile} />);
    }
  };

  return displayedImage(props.file);
};

const fileSelectorExpandedFile = {
  file: {
    height: '100%',
    objectFit: 'contain',
  },
} as const;

export const FilePreviewExpanded = (props: FilePreviewExpandedProps) => {
  const fileSelectorData = React.useContext(FileSelectorData);

  const displayedImage = (file: ExpandedFileFileResponse) => {
    switch (file.filetype) {
      case 'image':
      case 'vectorgraphic':
        return (
          <img
            src={fileSelectorData.rootPath + file.path}
            style={fileSelectorExpandedFile.file}
            onClick={() => props.onClose?.(undefined)}
            alt={file.displayName}
          />
        );
      case 'pdf':
        return (
          <iframe
            src={fileSelectorData.rootPath + file.path}
            style={fileSelectorExpandedFile.file}
            title={file.displayName}
          />
        );
    }
  };

  return displayedImage(props.file);
};
